import React from 'react';
import { Briefcase, ExternalLink, Package, ChevronRight, Folder } from 'lucide-react';
import { useTranslation } from '../../context/LanguageContext';

const MarketplaceViewWorldClass = ({ marketplaceCases, navigate }) => {
  const { language } = useTranslation();

  // Stats
  const totalCases = marketplaceCases.length;
  const basisCases = marketplaceCases.filter(c => c.afa_package_level === 'BASIS').length;
  const dokumentCases = marketplaceCases.filter(c => c.afa_package_level === 'DOKUMENT').length;
  const utvidetCases = marketplaceCases.filter(c => c.afa_package_level === 'UTVIDET').length;

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-6xl mx-auto px-8 py-10">
        
        {/* Header */}
        <header className="mb-10">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900 tracking-tight">
                {language === 'en' ? 'Lawyer for All' : 'Advokat for alle'}
              </h1>
              <p className="text-gray-500 text-sm mt-1">
                {totalCases} {totalCases === 1 
                  ? (language === 'en' ? 'case available' : 'sak tilgjengelig') 
                  : (language === 'en' ? 'cases available' : 'saker tilgjengelig')}
              </p>
            </div>
            <button
              onClick={() => navigate('/lexy/dashboard?tab=community')}
              className="flex items-center gap-2 px-4 py-2.5 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors text-sm font-medium"
            >
              <ExternalLink className="w-4 h-4" />
              Community
            </button>
          </div>
        </header>

        {/* Stats Row */}
        <div className="flex items-center gap-10 mb-10 pb-8 border-b border-gray-100">
          <div>
            <div className="text-3xl font-semibold text-gray-900">{totalCases}</div>
            <div className="text-sm text-gray-500">{language === 'en' ? 'New Cases' : 'Nye saker'}</div>
          </div>
          <div className="w-px h-10 bg-gray-200" />
          <div>
            <div className="text-3xl font-semibold text-gray-900">{basisCases}</div>
            <div className="text-sm text-gray-500">Basis</div>
          </div>
          <div className="w-px h-10 bg-gray-200" />
          <div>
            <div className="text-3xl font-semibold text-gray-900">{dokumentCases}</div>
            <div className="text-sm text-gray-500">{language === 'en' ? 'Document' : 'Dokument'}</div>
          </div>
          <div className="w-px h-10 bg-gray-200" />
          <div>
            <div className="text-3xl font-semibold text-gray-900">{utvidetCases}</div>
            <div className="text-sm text-gray-500">{language === 'en' ? 'Extended' : 'Utvidet'}</div>
          </div>
        </div>

        {/* Section Header */}
        <h2 className="text-lg font-semibold text-gray-900 mb-6">
          {language === 'en' ? 'Available Cases' : 'Tilgjengelige saker'}
        </h2>

        {/* Cases List */}
        {marketplaceCases.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center mx-auto mb-3">
              <Folder className="w-6 h-6 text-gray-400" />
            </div>
            <p className="text-gray-900 font-medium mb-1">
              {language === 'en' ? 'No marketplace cases' : 'Ingen marketplace-saker'}
            </p>
            <p className="text-gray-500 text-sm mb-4">
              {language === 'en' ? 'There are currently no available cases' : 'Det er for øyeblikket ingen tilgjengelige saker'}
            </p>
            <button
              onClick={() => navigate('/lexy/dashboard?tab=community')}
              className="text-sm text-slate-900 font-medium hover:underline inline-flex items-center gap-1"
            >
              {language === 'en' ? 'Visit Community' : 'Besøk Community'}
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
        ) : (
          <div className="space-y-0">
            {marketplaceCases.map((caseItem) => (
              <div
                key={caseItem.id}
                className="group flex items-center gap-4 py-4 cursor-pointer transition-all border-b border-gray-100 last:border-b-0 hover:bg-gray-50 px-3 -mx-3 rounded-lg"
                onClick={() => navigate(`/marketplace/case/${caseItem.id}`)}
              >
                {/* Icon */}
                <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Briefcase className="w-5 h-5 text-slate-600" />
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <h3 className="font-medium text-gray-900 truncate">
                      {caseItem.title || caseItem.topic}
                    </h3>
                    {caseItem.afa_package_level && (
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full ${
                        caseItem.afa_package_level === 'BASIS' ? 'bg-blue-50 text-blue-700' :
                        caseItem.afa_package_level === 'DOKUMENT' ? 'bg-violet-50 text-violet-700' :
                        'bg-emerald-50 text-emerald-700'
                      }`}>
                        <Package className="w-3 h-3" />
                        {caseItem.afa_package_level === 'BASIS' ? 'Basis' : 
                         caseItem.afa_package_level === 'DOKUMENT' ? (language === 'en' ? 'Document' : 'Dokument') : 
                         (language === 'en' ? 'Extended' : 'Utvidet')}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    {(caseItem.category || caseItem.subcategory) && (
                      <span>{caseItem.category}{caseItem.subcategory && ` · ${caseItem.subcategory}`}</span>
                    )}
                    {caseItem.client_name && (
                      <>
                        <span className="text-gray-300">•</span>
                        <span>{caseItem.client_name}</span>
                      </>
                    )}
                    {caseItem.time_since && (
                      <>
                        <span className="text-gray-300">•</span>
                        <span>{caseItem.time_since}</span>
                      </>
                    )}
                  </div>
                </div>

                {/* Price */}
                {caseItem.afa_package_price && (
                  <div className="text-right">
                    <div className="font-semibold text-gray-900">{caseItem.afa_package_price.toLocaleString('nb-NO')} kr</div>
                    <div className="text-xs text-gray-500">{language === 'en' ? 'Fixed price' : 'Fast pris'}</div>
                  </div>
                )}

                <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-gray-500 transition-colors" />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MarketplaceViewWorldClass;
